// components/notifications/NotificationListItem.tsx

'use client'

import Link from 'next/link'
import { type Notification, PRIORITY_DOT_COLOR } from '@/lib/types/notifications'
import { NotificationTypeBadge } from './NotificationTypeBadge'
import { cn } from '@/lib/utils'

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  return `${d}d`
}

export function NotificationListItem({
  notification,
  onMarkRead
}: {
  notification: Notification
  onMarkRead?: (id: string) => void
}) {
  const handleClick = () => {
    if (!notification.is_read && onMarkRead) onMarkRead(notification.id)
  }
  const body = (
    <div className="flex items-start gap-3 py-3 px-3">
      <span
        className={cn(
          'mt-1.5 inline-block h-2 w-2 rounded-full flex-shrink-0',
          PRIORITY_DOT_COLOR[notification.priority],
          notification.is_read && 'opacity-30'
        )}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <NotificationTypeBadge type={notification.notification_type} />
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            {timeAgo(notification.created_at)}
          </span>
          {!notification.is_read && (
            <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">●</span>
          )}
        </div>
        <h4 className={cn('mt-1 text-sm font-semibold', notification.is_read && 'opacity-70')}>
          {notification.title}
        </h4>
        <p
          className={cn(
            'mt-0.5 text-xs text-zinc-600 dark:text-zinc-300 line-clamp-2',
            notification.is_read && 'opacity-70'
          )}
        >
          {notification.message}
        </p>
        {notification.action_url && notification.action_label && (
          <Link
            href={notification.action_url}
            onClick={handleClick}
            className="mt-2 inline-block text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            {notification.action_label} →
          </Link>
        )}
      </div>
    </div>
  )
  if (notification.action_url) {
    return body
  }
  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
    >
      {body}
    </button>
  )
}
